// SCR-CLS-03/04/05: owner operating expenses, managed categories and linked corrections.
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  FormGrid,
  Input,
  List,
  ListRow,
  Notice,
  QuickActions,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import {
  EXPENSES,
  categoryLabel,
  correctExpense,
  loadExpenseCategories,
  loadExpenses,
  recordExpense,
  saveExpenseCategory,
  toggleExpenseCategory,
  type ExpenseRow,
} from '../../data/expenses';
import { loadUsers } from '../../data/settings';
import { CASH, loadCashPlaces } from '../../data/cash';
import { loadTrucks } from '../../data/rounds';
import type { Language } from '../../i18n';
import { Code } from '../../i18n/Isolate';
import { businessToday, formatDate, formatMoney } from '../../lib/format';
import { parseAmount, useTab } from '../catalog/common';

type Source = 'all' | 'round' | 'garage' | 'bank' | 'personal';
type SheetState = 'expense' | 'correction' | 'category' | null;

function useWords() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  return { t, lang, money: (n: number | string) => formatMoney(n, lang) };
}

function ExpenseSheet({
  onClose,
  categories,
  trucks,
  users,
  cashPlaces,
}: {
  onClose: () => void;
  categories: Awaited<ReturnType<typeof loadExpenseCategories>>;
  trucks: Awaited<ReturnType<typeof loadTrucks>>;
  users: Awaited<ReturnType<typeof loadUsers>>;
  cashPlaces: Awaited<ReturnType<typeof loadCashPlaces>>;
}) {
  const { t, lang, money } = useWords();
  const qc = useQueryClient();
  const [step, setStep] = useState<'form' | 'summary'>('form');
  const [category, setCategory] = useState(categories.find((c) => c.active && !c.fixed)?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(businessToday());
  const [truck, setTruck] = useState('');
  const [source, setSource] = useState<'garage' | 'bank' | 'personal'>('garage');
  const [payer, setPayer] = useState('');
  const [notes, setNotes] = useState('');
  const [problem, setProblem] = useState('');
  const value = parseAmount(amount);
  const selectedCategory = categories.find((c) => c.id === category);
  const sourceBalance =
    source === 'personal' ? null : Number(cashPlaces.find((place) => place.kind === source)?.balance ?? 0);
  const mutation = useMutation({
    mutationFn: () =>
      recordExpense({
        category,
        amount: value ?? 0,
        funding: source,
        date,
        truck: truck || null,
        payer: source === 'personal' ? payer || null : null,
        notes: notes || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES });
      void qc.invalidateQueries({ queryKey: ['cash'] });
      void qc.invalidateQueries({ queryKey: ['rounds'] });
      onClose();
    },
    onError: () => setProblem(t('err.generic')),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem('');
    if (
      !category ||
      !value ||
      value <= 0 ||
      Math.round(value * 100) !== value * 100 ||
      !date ||
      (source === 'personal' && !payer)
    ) {
      setProblem(t('err.required'));
      return;
    }
    setStep('summary');
  };
  return (
    <BottomSheet title={t('p7.record')} open onClose={onClose} busy={mutation.isPending}>
      <form onSubmit={submit}>
        {step === 'form' ? (
          <>
            <FormGrid>
              <Field label={t('p7.category')}>
                {({ id }) => (
                  <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                    {categories
                      .filter((c) => c.active && !c.fixed)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
              <Field label={t('p7.amount')}>
                {({ id }) => (
                  <Input
                    id={id}
                    numeric
                    inputMode="decimal"
                    dir="ltr"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>
            </FormGrid>
            <FormGrid>
              <Field label={t('p7.date')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="date"
                    dir="ltr"
                    value={date}
                    max={businessToday()}
                    onChange={(e) => setDate(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('p7.truck')}>
                {({ id }) => (
                  <Select id={id} value={truck} onChange={(e) => setTruck(e.target.value)}>
                    <option value="">{t('p7.general')}</option>
                    {trucks
                      .filter((x) => x.active)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            </FormGrid>
            <Field label={t('p7.details')}>
              {({ id }) => <Input id={id} dir="auto" value={notes} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
            <Field label={t('p7.source')}>
              {({ id }) => (
                <Select id={id} value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                  <option value="garage">{t('p7.garage')}</option>
                  <option value="bank">{t('p7.bank')}</option>
                  <option value="personal">{t('p7.personal')}</option>
                </Select>
              )}
            </Field>
            {sourceBalance !== null && (
              <Notice tone="info">
                {t('p7.available')}: {money(sourceBalance)}
              </Notice>
            )}
            {source === 'personal' && (
              <Field label={t('p7.payer')}>
                {({ id }) => (
                  <Select id={id} value={payer} onChange={(e) => setPayer(e.target.value)}>
                    <option value="">—</option>
                    {users
                      .filter((u) => u.active && u.role === 'seller')
                      .map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.display_name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            )}
            {source === 'personal' && <Notice tone="info">{t('p7.payerNote')}</Notice>}
            {problem && <Notice tone="danger">{problem}</Notice>}
            <SheetActions>
              <Button type="button" onClick={onClose}>
                {t('p7.cancel')}
              </Button>
              <Button variant="main" type="submit">
                {t('p7.next')}
              </Button>
            </SheetActions>
          </>
        ) : (
          <>
            <Notice tone="info">
              {money(value ?? 0)} · {selectedCategory?.name} · {t(`p7.${source}`)} · {formatDate(date, lang)}
              {source === 'personal' && payer ? ` · ${users.find((u) => u.user_id === payer)?.display_name ?? ''}` : ''}
              {sourceBalance !== null ? ` · ${t('p7.cashAfter')}: ${money(sourceBalance - (value ?? 0))}` : ''}
            </Notice>
            <p>
              {truck ? trucks.find((x) => x.id === truck)?.code : t('p7.general')}
              {notes ? ` · ${notes}` : ''}
            </p>
            {problem && <Notice tone="danger">{problem}</Notice>}
            <SheetActions>
              <Button type="button" onClick={() => setStep('form')}>
                {t('p7.back')}
              </Button>
              <Button variant="main" busy={mutation.isPending} onClick={() => mutation.mutate()}>
                {t('p7.confirm')}
              </Button>
            </SheetActions>
          </>
        )}
      </form>
    </BottomSheet>
  );
}

function CorrectionSheet({ expense, onClose }: { expense: ExpenseRow; onClose: () => void }) {
  const { t, money } = useWords();
  const qc = useQueryClient();
  const [step, setStep] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [problem, setProblem] = useState('');
  const mutation = useMutation({
    mutationFn: () => correctExpense(expense.id, parseAmount(amount) ?? 0, description),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES });
      void qc.invalidateQueries({ queryKey: ['cash'] });
      onClose();
    },
    onError: () => setProblem(t('err.generic')),
  });
  const value = parseAmount(amount);
  return (
    <BottomSheet title={`${t('p7.correct')} · ${expense.number}`} open onClose={onClose} busy={mutation.isPending}>
      {step ? (
        <>
          <Notice tone="info">
            {money(value ?? 0)} · {expense.number} · {t(`p7.${expense.source}`)}
          </Notice>
          <p>{description}</p>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={() => setStep(false)}>{t('p7.back')}</Button>
            <Button variant="main" busy={mutation.isPending} onClick={() => mutation.mutate()}>
              {t('p7.confirm')}
            </Button>
          </SheetActions>
        </>
      ) : (
        <>
          <List label={expense.number}>
            <ListRow
              lead={{ icon: 'file' }}
              title={categoryLabel(t, expense.category, expense.category_key)}
              sub={t(`p7.${expense.source}`)}
              value={money(expense.amount)}
            />
          </List>
          <Field label={t('p7.amount')}>
            {({ id }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                dir="ltr"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('p7.details')}>
            {({ id }) => (
              <Input id={id} dir="auto" value={description} onChange={(e) => setDescription(e.target.value)} />
            )}
          </Field>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={onClose}>{t('p7.cancel')}</Button>
            <Button
              variant="main"
              onClick={() => {
                if (!value || value <= 0 || value > expense.amount || !description.trim()) {
                  setProblem(t('err.required'));
                  return;
                }
                setProblem('');
                setStep(true);
              }}
            >
              {t('p7.next')}
            </Button>
          </SheetActions>
        </>
      )}
    </BottomSheet>
  );
}

function CategorySheet({ onClose }: { onClose: () => void }) {
  const { t } = useWords();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [problem, setProblem] = useState('');
  const mutation = useMutation({
    mutationFn: () => saveExpenseCategory(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...EXPENSES, 'categories'] });
      onClose();
    },
    onError: () => setProblem(t('err.generic')),
  });
  return (
    <BottomSheet title={t('p7.newcat')} open onClose={onClose} busy={mutation.isPending}>
      <Field label={t('p7.categoryName')}>
        {({ id }) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      {problem && <Notice tone="danger">{problem}</Notice>}
      <SheetActions>
        <Button onClick={onClose}>{t('p7.cancel')}</Button>
        <Button
          variant="main"
          busy={mutation.isPending}
          onClick={() => (name.trim() ? mutation.mutate() : setProblem(t('err.required')))}
        >
          {t('p7.confirm')}
        </Button>
      </SheetActions>
    </BottomSheet>
  );
}

export function ExpensesPage() {
  const { t, lang, money } = useWords();
  const [tab, setTab] = useTab(['expenses', 'categories'] as const, 'expenses');
  const [source, setSource] = useState<Source>('all');
  const [truckFilter, setTruckFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sheet, setSheet] = useState<SheetState>(null);
  const [correcting, setCorrecting] = useState<ExpenseRow | null>(null);
  const qc = useQueryClient();
  const expenses = useQuery({ queryKey: [...EXPENSES, 'list'], queryFn: loadExpenses });
  const categories = useQuery({ queryKey: [...EXPENSES, 'categories'], queryFn: loadExpenseCategories });
  const trucks = useQuery({ queryKey: ['rounds', 'trucks'], queryFn: loadTrucks });
  const users = useQuery({ queryKey: ['settings', 'users'], queryFn: loadUsers });
  const cashPlaces = useQuery({ queryKey: [...CASH, 'places'], queryFn: loadCashPlaces });
  const toggle = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => toggleExpenseCategory(v.id, v.active),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...EXPENSES, 'categories'] }),
  });
  const month = businessToday().slice(0, 7);
  const thisMonth = (expenses.data ?? []).filter((e) => e.business_date.startsWith(month) && !e.in_stock_cost);
  const totalFor = (items: ExpenseRow[]) => items.reduce((sum, e) => sum + (e.correction ? -e.amount : e.amount), 0);
  const total = totalFor(thisMonth);
  const filtered = useMemo(
    () =>
      (expenses.data ?? []).filter(
        (e) =>
          (source === 'all' || e.source === source) &&
          (truckFilter === 'all' || e.truck_id === truckFilter) &&
          (categoryFilter === 'all' || e.category === categoryFilter),
      ),
    [expenses.data, source, truckFilter, categoryFilter],
  );
  const categoriesReady = categories.data ?? [];
  return (
    <>
      <SummaryCard
        label={t('p7.summary')}
        value={money(total)}
        grid
        figures={[
          { label: t('p7.rounds'), value: money(totalFor(thisMonth.filter((e) => e.source === 'round'))) },
          { label: t('p7.garage'), value: money(totalFor(thisMonth.filter((e) => e.source === 'garage'))) },
          { label: t('p7.bank'), value: money(totalFor(thisMonth.filter((e) => e.source === 'bank'))) },
          { label: t('p7.personal'), value: money(totalFor(thisMonth.filter((e) => e.source === 'personal'))) },
        ]}
      />
      <QuickActions
        actions={
          tab === 'expenses'
            ? [
                {
                  id: 'record-expense',
                  label: t('p7.record'),
                  icon: 'plus',
                  main: true,
                  onSelect: () => setSheet('expense'),
                },
              ]
            : [
                {
                  id: 'new-expense-category',
                  label: t('p7.newcat'),
                  icon: 'plus',
                  main: true,
                  onSelect: () => setSheet('category'),
                },
              ]
        }
      />
      <Tabs
        label={t('p.expenses')}
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { id: 'expenses', label: t('p7.tab.expenses') },
          { id: 'categories', label: t('p7.tab.categories') },
        ]}
      />
      {tab === 'expenses' ? (
        <>
          <Chips
            label={t('p7.source')}
            selected={[source]}
            onToggle={(v) => setSource(v as Source)}
            items={[
              { id: 'all', label: t('f.all') },
              { id: 'round', label: t('p7.rounds') },
              { id: 'garage', label: t('p7.garage') },
              { id: 'bank', label: t('p7.bank') },
              { id: 'personal', label: t('p7.personal') },
            ]}
          />
          <FormGrid>
            <Field label={t('p7.truck')}>
              {({ id }) => (
                <Select id={id} value={truckFilter} onChange={(e) => setTruckFilter(e.target.value)}>
                  <option value="all">{t('p7.allTrucks')}</option>
                  {(trucks.data ?? []).map((truck) => (
                    <option key={truck.id} value={truck.id}>
                      {truck.code}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('p7.category')}>
              {({ id }) => (
                <Select id={id} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="all">{t('p7.allCategories')}</option>
                  {categoriesReady.map((category) => (
                    <option key={category.id} value={category.name}>
                      {categoryLabel(t, category.name, category.category_key)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </FormGrid>
          {expenses.isError ? (
            <Notice tone="danger">{t('err.generic')}</Notice>
          ) : !expenses.data ? (
            <SkeletonList label={t('loading')} />
          ) : filtered.length === 0 ? (
            <EmptyState icon="file" title={t('p7.empty')} text={t('p7.emptyText')} />
          ) : (
            <List label={t('p7.tab.expenses')}>
              {filtered.map((e) => (
                <ListRow
                  key={e.id}
                  testId={`expense-${e.number}`}
                  lead={{ icon: 'file' }}
                  title={
                    <>
                      {categoryLabel(t, e.category, e.category_key)} · <Code>{e.number}</Code>
                    </>
                  }
                  sub={
                    <>
                      {formatDate(e.business_date, lang)} ·{' '}
                      {e.truck_code ? <Code>{e.truck_code}</Code> : t('p7.general')}
                      {e.round_number && (
                        <>
                          {' '}
                          · <Code>{e.round_number}</Code>
                        </>
                      )}
                      {e.notes ? ` · ${e.notes}` : ''}
                    </>
                  }
                  value={money(e.correction ? -e.amount : e.amount)}
                  actions={
                    <>
                      <Badge>{t(e.source === 'round' ? 'p7.rounds' : `p7.${e.source}`)}</Badge>
                      {e.correction && <Badge>{t('p7.correction')}</Badge>}
                      {e.in_stock_cost && <Badge>{t('p7.inStock')}</Badge>}
                      {!e.correction && !e.in_stock_cost && e.source === 'round' && <Badge>{t('p7.readOnly')}</Badge>}
                      {e.correctable && (
                        <Button pill onClick={() => setCorrecting(e)}>
                          {t('p7.correct')}
                        </Button>
                      )}
                    </>
                  }
                />
              ))}
            </List>
          )}
        </>
      ) : !categories.data ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <List label={t('p7.tab.categories')}>
          {categoriesReady.map((c) => (
            <ListRow
              key={c.id}
              lead={{ icon: c.fixed ? 'users' : 'file' }}
              title={categoryLabel(t, c.name, c.category_key)}
              sub={c.fixed ? t('p7.fixed') : c.active ? t('p7.enabled') : t('p7.disabled')}
              actions={
                c.fixed ? (
                  <Badge>{t('p7.fixed')}</Badge>
                ) : (
                  <Button pill busy={toggle.isPending} onClick={() => toggle.mutate({ id: c.id, active: !c.active })}>
                    {t(c.active ? 'p7.deactivate' : 'p7.reactivate')}
                  </Button>
                )
              }
            />
          ))}
        </List>
      )}
      {sheet === 'expense' && categories.data && trucks.data && users.data && cashPlaces.data && (
        <ExpenseSheet
          onClose={() => setSheet(null)}
          categories={categories.data}
          trucks={trucks.data}
          users={users.data}
          cashPlaces={cashPlaces.data}
        />
      )}
      {sheet === 'category' && <CategorySheet onClose={() => setSheet(null)} />}
      {correcting && <CorrectionSheet expense={correcting} onClose={() => setCorrecting(null)} />}
    </>
  );
}
